import Box from '@mui/material/Box';
import { ListTypesAPI } from '@openx/types/inventoryList.ts';
import { type UrlTargeting, UrlTargetingType } from '@openx/types/targeting/domains.ts';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../utils/context/TargetingContext.tsx';
import { DimensionChip } from '../shared/DimensionChip/DimensionChip.tsx';
import { EmptyFilterMessage } from '../shared/EmptyFilterMessage/EmptyFilterMessage.tsx';
import { FiltersTitle } from '../shared/FiltersTitle/FiltersTitle.tsx';
import { InventoryListItems } from '../shared/InventoryListItems/InventoryListItems.tsx';
import { SelectedItems } from '../shared/SelectedItems/SelectedItems.tsx';
import { Switch } from '../shared/Switch/Switch.tsx';
import type { TargetingItemsProps } from '../types.ts';
import { getDomainsItemsTitle } from './utils/getDomainsItemsTitle.ts';
import { getDomainTargetingLabel } from './utils/getDomainTargetingLabel.ts';

type DomainsItemsProps = {
  accountUid?: string;
} & TargetingItemsProps<UrlTargeting>;

export const DomainsItems = ({
  targetingParams,
  readonly = true,
  onChange = noop,
  accountUid = '',
  disabledReasonMessage,
}: DomainsItemsProps) => {
  const { t } = useTranslation();
  const [inventoryListItems, setInventoryListItems] = useState<string[]>([]);
  const { isOa } = useTargetingContext();
  const { type, urls, inventorylist_id, domain_targeting_option } = targetingParams;
  const urlsLength = urls?.length || 0;
  const inventoryListItemsLength = inventorylist_id ? inventoryListItems.length : 0;
  const switchState = type === UrlTargetingType.blacklist;
  const typeLabel = getDomainsItemsTitle({
    urlsLength,
    inventoryListItemsLength,
    isBlacklist: switchState,
    isOa: !!isOa,
    t,
  });

  const filtersTitle = urlsLength ? typeLabel : '';
  const itemsLength = urlsLength + inventoryListItemsLength;

  const handleChange = useCallback(
    (values: string[]) => {
      onChange({
        type,
        domain_targeting_option,
        urls: values,
      });
    },
    [onChange, type, domain_targeting_option]
  );

  const domainTargetingLabel = useMemo(
    () => getDomainTargetingLabel(domain_targeting_option, t),
    [t, domain_targeting_option]
  );

  return urlsLength > 0 || (isOa && inventorylist_id) ? (
    <Box data-test="sub-section">
      <FiltersTitle
        onClear={() => handleChange([])}
        title={t('Domains [ {count} ]{type}', {
          count: itemsLength,
          type: ` - ${domainTargetingLabel}`,
        })}
        readonly={readonly}
        marginTopValue={1}
        disabledReasonMessage={disabledReasonMessage}
      />
      {!readonly && (
        <Switch
          switchState={switchState}
          onSwitchChange={() =>
            onChange({
              type: type === UrlTargetingType.blacklist ? UrlTargetingType.whitelist : UrlTargetingType.blacklist,
              domain_targeting_option,
              urls,
            })
          }
          switchOffLabel={t('allow')}
          switchOnLabel={t('block')}
          groupTitle={t('the following:')}
        />
      )}

      {isOa && inventorylist_id && (
        <>
          <InventoryListItems
            type={targetingParams.type}
            readonly={true}
            inventorylistId={inventorylist_id}
            setInventoryListItems={setInventoryListItems}
            listType={ListTypesAPI.DOMAIN}
            accountUid={accountUid}
            disabledReasonMessage={disabledReasonMessage}
          />

          {urlsLength > 0 && <DimensionChip isInsideOption targetingDimension={Criteria.ANY} />}
        </>
      )}

      {urlsLength > 0 && (
        <SelectedItems
          filtersTitle={filtersTitle}
          isAddIcon={!switchState}
          selectedItems={urls || []}
          readonly={readonly}
          handleChange={handleChange}
          dataTest={filtersTitle}
        />
      )}
    </Box>
  ) : (
    <EmptyFilterMessage text={t('No filters applied.')} />
  );
};
